import { SocketClient } from "cbcore-ts"
import { NO, UITextView, UIView, YES } from "uicore-ts"
import { RHCore } from "../../Core/RHCore"
import { CBDataViewCellDescriptorWithObject } from "../../Custom components/CB/CBDataView"
import { CBDialogViewShower } from "../../Custom components/CB/CBDialogViewShower"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHDataView } from "../../Custom components/RH/RHDataView"
import { RHButtons, RHFont, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHTextSanitizer } from "../../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHCompanyAdministrationViewTokens } from "../../Custom components/RH/Utils_slop/RHViewTokens"
import { RHCompanyMember } from "../../SocketClientFunctions"
import { RHCompanyMemberPresentation } from "./CompanyMemberPresentation"
import { CompanyRoleBadgesView } from "./CompanyRoleBadgesView"
import { MemberManagementDialogView } from "./MemberManagementDialogView"


type CompanyMemberTableRow = RHCompanyMember & {
    nameText: string
    nameSearchText: string
    emailText: string
    rolesSearchText: string
    statusText: string
}


class CompanyMemberActionsView extends UIView {

    readonly removeButton = new RHButton().configuredWithObject({
        titleLabel: {
            text: "Eemalda",
            style: {
                whiteSpace: "nowrap",
                fontWeight: RHButtons.tableActionTitleFontWeight
            }
        },
        variant: RHButtons.destructiveUtilityVariant,
        contentPadding: RHButtons.tableActionContentPadding,
        style: { fontSize: RHButtons.tableActionFontSize }
    }).addedAsSubviewToView(this)

    readonly manageButton = new RHButton().configuredWithObject({
        titleLabel: {
            text: "Halda",
            style: {
                whiteSpace: "nowrap",
                fontWeight: RHButtons.tableActionTitleFontWeight
            }
        },
        variant: RHButtons.tableActionVariant,
        contentPadding: RHButtons.tableActionContentPadding,
        style: { fontSize: RHButtons.tableActionFontSize }
    }).addedAsSubviewToView(this)

    manageWasPressed?: () => void
    removeWasPressed?: () => void


    constructor(elementID?: string) {
        super(elementID)
        this.manageButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.manageWasPressed?.()
        }
        this.removeButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.removeWasPressed?.()
        }
        this.manageButton.titleLabel.textAlignment = UITextView.textAlignment.center
        this.removeButton.titleLabel.textAlignment = UITextView.textAlignment.center
    }


    override layoutSubviews() {
        super.layoutSubviews()
        const visibleButtons = [this.removeButton, this.manageButton].filter(
            button => !button.hidden
        )
        this.contentBounds
            .rectangleWithHeight(RHButtons.tableActionHeight, 0.5)
            .framesByDistributingViewsAsRow(
                visibleButtons,
                RHSpacing.small,
                visibleButtons.map(() => RHButtons.tableActionWidth),
                0.5
            )
    }


    override intrinsicContentHeight() {
        return RHButtons.tableActionHeight
    }

}


export class CompanyAdministrationMembersView extends UIView {

    readonly dataView = new RHDataView<CompanyMemberTableRow>()
        .configuredWithObject({
            noDataMessage: "Liikmed puuduvad",
            hasResizableColumns: NO,
            tableView: { allRowsHaveEqualHeight: NO },
            maximumIntrinsicContentHeight: 520
        })
        .addedAsSubviewToView(this)

    override _focusManager = new RHFocusManager(() => [
        this.dataView.searchTextField,
        this.dataView._gridContainerView
    ])

    companyId = ""
    administratorCount = 0
    isEligibleToActAsProcurer = YES
    currentUserId?: string
    isCurrentUserLeaveButtonEnabled = NO
    currentUserLeaveButtonHoverText?: string
    currentUserLeaveWasPressed?: () => void
    membersDidChange?: () => void
    _members: CompanyMemberTableRow[] = []
    _allowsMemberManagement = YES


    constructor(elementID?: string) {
        super(elementID)
        this.dataView.searchTextField.placeholderText =
            "Otsi nime, e-posti, rolli või staatuse järgi..."
        this.dataView.descriptors = this._descriptors()
    }


    set allowsMemberManagement(allowsMemberManagement: boolean) {
        if (this._allowsMemberManagement === allowsMemberManagement) {
            return
        }
        this._allowsMemberManagement = allowsMemberManagement
        this.dataView.descriptors = this._descriptors()
        this.setNeedsLayout()
    }


    get allowsMemberManagement(): boolean {
        return this._allowsMemberManagement
    }


    set members(members: RHCompanyMember[]) {
        this._members = members.map(member => {
            const rolesSearchText = CompanyRoleBadgesView
                .displayLabelsForRoles(member.roles, NO)
                .join(", ")
            const identityPresentation =
                RHCompanyMemberPresentation.identityPresentationForPerson(member)
            let statusText = "Aktiivne"
            if (member.status === "inactive") {
                statusText = "Mitteaktiivne"
            }
            else if (member.status === "pending") {
                statusText = "Ootel"
            }
            return {
                ...member,
                nameText: identityPresentation.escapedDisplayName ?? "-",
                nameSearchText: identityPresentation.displayName ?? "-",
                emailText: identityPresentation.escapedEmail || "-",
                rolesSearchText,
                statusText
            }
        })
        this.dataView.data = this._members
        this.setNeedsLayout()
    }


    get members(): RHCompanyMember[] {
        return [...this._members]
    }


    _descriptors() {
        const nameDescriptor =
            CBDataViewCellDescriptorWithObject<CompanyMemberTableRow>({
                title: "Nimi",
                keyPath: "nameSearchText",
                allowSorting: YES,
                weight: 1,
                minimumWidth:
                    RHCompanyAdministrationViewTokens.affiliationColumnMinimumWidths.name,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    cellView.text = dataItem.nameText
                }
            })
        const emailDescriptor =
            CBDataViewCellDescriptorWithObject<CompanyMemberTableRow>({
                title: "E-post",
                keyPath: "email",
                allowSorting: YES,
                weight: 1,
                minimumWidth:
                    RHCompanyAdministrationViewTokens.affiliationColumnMinimumWidths.email,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                initCell: cellView => {
                    cellView.titleLabel!.configureWithObject({
                        containerStyle: { wordBreak: "break-all" },
                        style: { wordBreak: "break-all" }
                    })
                },
                updateCell: (cellView, dataItem) => {
                    cellView.text = dataItem.emailText
                    let fontWeight: string = RHFont.weightRegular
                    if (dataItem.userId === this.currentUserId) {
                        fontWeight = RHFont.weightBold
                    }
                    cellView.titleLabel!.configureWithObject({
                        style: { fontWeight }
                    })
                }
            })
        const rolesDescriptor =
            CBDataViewCellDescriptorWithObject<CompanyMemberTableRow, CompanyRoleBadgesView>({
                title: "Roll",
                keyPath: "rolesSearchText",
                allowSorting: NO,
                weight: 1,
                minimumWidth:
                    RHCompanyAdministrationViewTokens.affiliationColumnMinimumWidths.role,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                initCustomView: () => new CompanyRoleBadgesView(),
                updateCell: (cellView, dataItem) => {
                    cellView.customView.updateWithRoles(
                        dataItem.roles,
                        this.isEligibleToActAsProcurer,
                        NO
                    )
                }
            })
        const statusDescriptor =
            CBDataViewCellDescriptorWithObject<CompanyMemberTableRow>({
                title: "Staatus",
                keyPath: "statusText",
                allowSorting: YES,
                weight: 1,
                minimumWidth:
                    RHCompanyAdministrationViewTokens.affiliationColumnMinimumWidths.status
            })
        const actionsDescriptor =
            CBDataViewCellDescriptorWithObject<CompanyMemberTableRow, CompanyMemberActionsView>({
                title: "Tegevused",
                keyPath: "nameSearchText",
                allowSorting: NO,
                weight: 0,
                minimumWidth: RHButtons.tableActionWidth * 2 + RHSpacing.small,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                initCustomView: () => new CompanyMemberActionsView(),
                updateCell: (cellView, dataItem) => {
                    this._configureActionsView(cellView.customView, dataItem)
                }
            })
        return [nameDescriptor, emailDescriptor, rolesDescriptor, statusDescriptor, actionsDescriptor]
    }


    _configureActionsView(actionsView: CompanyMemberActionsView, member: CompanyMemberTableRow) {
        actionsView.removeButton.titleLabel.text = "Eemalda"
        actionsView.manageButton.titleLabel.text = "Halda"
        actionsView.removeButton.hidden = NO
        actionsView.manageButton.hidden = NO
        actionsView.manageButton.enabled = YES
        actionsView.removeButton.enabled = YES
        actionsView.manageButton.hoverText = undefined
        actionsView.removeButton.hoverText = undefined
        actionsView.manageWasPressed = undefined
        actionsView.removeWasPressed = undefined

        if (member.userId === this.currentUserId) {
            actionsView.removeButton.titleLabel.text = "Lahku"
            actionsView.manageButton.enabled = this._allowsMemberManagement
            if (!this._allowsMemberManagement) {
                actionsView.manageButton.hoverText =
                    "Enda rolle saab hallata ainult ettevõtte peakasutaja."
            }
            actionsView.removeButton.enabled = this.isCurrentUserLeaveButtonEnabled
            actionsView.removeButton.hoverText = this.currentUserLeaveButtonHoverText
            actionsView.manageWasPressed = () => this.openMemberDialog(member)
            actionsView.removeWasPressed = this.currentUserLeaveWasPressed
            actionsView.setNeedsLayout()
            return
        }

        if (!this._allowsMemberManagement) {
            actionsView.removeButton.hidden = YES
            actionsView.manageButton.hidden = YES
            actionsView.setNeedsLayout()
            return
        }
        actionsView.manageWasPressed = () => this.openMemberDialog(member)
        actionsView.removeWasPressed = () => this._confirmAndEndMembership(member)
        actionsView.setNeedsLayout()
    }


    openMemberDialog(member: RHCompanyMember) {
        const dialogView = new MemberManagementDialogView()
        dialogView.configureForMember(
            member,
            CompanyAdministrationMembersView._preventsAdministratorRemoval(
                member,
                this.administratorCount
            ),
            this.isEligibleToActAsProcurer
        )
        dialogView.saveButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            const roles = dialogView.selectedRoles
            if (roles.length === 0) {
                CBDialogViewShower.alert("Vali vähemalt üks kasutaja roll.")
                return
            }
            dialogView.loading = YES
            try {
                const response = await SocketClient.ChangeCompanyMemberRole({
                    companyId: this.companyId,
                    userId: member.userId,
                    roles
                })
                if (response.result?.success) {
                    dialogView.dialogView?.dismiss()
                    this.membersDidChange?.()
                }
                else {
                    this._showError(response, "Liikme rolli muutmine ebaõnnestus.")
                }
            }
            catch (error) {
                console.error("Company member role change failed:", error)
                CBDialogViewShower.alert("Liikme rolli muutmine ebaõnnestus.")
            }
            finally {
                dialogView.loading = NO
            }
        }
        CBDialogViewShower.showDialog(dialogView, undefined, NO)
    }


    _confirmAndEndMembership(member: CompanyMemberTableRow) {
        const shower = CBDialogViewShower.showQuestionDialog(
            `Kas eemaldad kasutaja ${RHTextSanitizer.escapedHTML(member.name)} ettevõttest?`
        )
        shower.yesButtonWasPressed = async () => {
            shower.dialogView.dismiss()
            try {
                const response = await SocketClient.EndCompanyMembership({
                    companyId: this.companyId,
                    userId: member.userId
                })
                if (response.result?.success) {
                    this.membersDidChange?.()
                }
                else {
                    this._showError(response, "Liikme eemaldamine ebaõnnestus.")
                }
            }
            catch (error) {
                console.error("End company membership failed:", error)
                CBDialogViewShower.alert("Liikme eemaldamine ebaõnnestus.")
            }
        }
    }


    _showError(response: { errorResult?: unknown }, fallback: string) {
        CBDialogViewShower.alert(
            RHCore.sharedInstance.errorMessageFromResponse(response, fallback) ?? fallback
        )
    }


    override layoutSubviews() {
        super.layoutSubviews()
        this.dataView.frame = this.contentBounds.rectangleWithHeight(this.dataView)
    }


    override intrinsicContentHeight(constrainingWidth = 0) {
        return this.dataView.intrinsicContentHeight(constrainingWidth)
    }


    static _preventsAdministratorRemoval(
        member: Pick<RHCompanyMember, "roles">,
        administratorCount: number
    ): boolean {
        return member.roles.includes("administrator") && administratorCount === 1
    }

}
